<template>
    <div class="animated fadeIn crm-page">
        <ComponentHeader
            title="CRM"
            :hasSearch="true"
            :hasCreate="true"
            :hasReload="false"
            :hasback="false"
            createButtonText="Add to CRM"
            @search-query="(val) => (searchQuery = val)"
            @create-press="showAddCrmModal"
        >
            <template #heading-before-search>
                <div class="crm-filters-wrapper">
                    <b-form-select
                        v-model="statusFilter"
                        :options="statusOptions"
                        class="filter-select crm-filter-select"
                    ></b-form-select>
                    <b-form-select
                        v-model="planFilter"
                        :options="planOptions"
                        class="filter-select crm-filter-select"
                    ></b-form-select>
                </div>
            </template>
        </ComponentHeader>

        <div v-if="loading" class="crm-loading d-flex flex-column align-items-center justify-content-center py-5">
            <atom-spinner
                :animation-duration="1000"
                :size="60"
                color="#ff1d5e"
                class="my-5"
            />
            <p class="text-muted mb-0">Loading...</p>
        </div>
        <b-card v-else class="card-wrap crm-table-card mt-2" border-variant="info">
            <div class="table-wrapper">
                <b-table
                    id="crm-table"
                    :items="filteredCrmList"
                    :fields="tableFields"
                    responsive="sm"
                    hover
                    :bordered="true"
                    :striped="false"
                    class="crm-table"
                    tbody-tr-class="crm-row"
                    show-empty
                    empty-text="No CRM records found."
                >
                <template #cell(account)="data">
                    <div class="account-cell">
                        <span class="avatar" :title="data.item.initials">{{
                            data.item.initials
                        }}</span>
                        <div class="account-info">
                            <span class="account-name">{{
                                data.item.name
                            }}</span>
                            <span class="account-company">{{
                                data.item.company
                            }}</span>
                        </div>
                    </div>
                </template>

                <template #cell(plan)="data">
                    <b-badge pill variant="light" class="plan-badge">{{
                        data.item.plan
                    }}</b-badge>
                </template>

                <template #cell(status)="data">
                    <b-badge
                        pill
                        :class="[
                            'status-badge',
                            'status-' +
                                (data.item.status || '')
                                    .toLowerCase()
                                    .replace(/\s/g, '-'),
                        ]"
                    >
                        {{ data.item.status }}
                    </b-badge>
                </template>

                <template #cell(mrr)="data">
                    <span class="mrr-value">${{ data.item.mrr }}</span>
                </template>

                <template #cell(lastPayment)="data">
                    <span class="last-payment">{{
                        data.item.lastPayment
                    }}</span>
                </template>

                <template #cell(tickets)="data">
                    <span v-if="data.item.tickets > 0" class="tickets-open">
                        <span class="ticket-dot"></span>
                        {{ data.item.tickets }} Open
                    </span>
                    <span v-else class="tickets-none">No Tickets</span>
                </template>
                <template slot="cell(actions)" slot-scope="data">
                    <ActionButtonGroup
                        :showView="true"
                        @view="viewCRM(data.item)"
                    />
                </template>
                </b-table>
            </div>
        </b-card>

        <!-- Add to CRM Modal -->
        <b-modal
            id="addCrmModal"
            ref="addCrmModal"
            size="md"
            centered
            title="Add to CRM"
            hide-footer
            modal-class="crm-modal"
            @hidden="resetAddCrmForm"
        >
            <b-form @submit.prevent="handleAddCrm">
                <b-form-group label="Name" label-for="crm-name">
                    <b-form-input
                        id="crm-name"
                        v-model="addCrmForm.name"
                        placeholder="e.g., John Doe"
                        required
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Company" label-for="crm-company">
                    <b-form-input
                        id="crm-company"
                        v-model="addCrmForm.company"
                        placeholder="e.g., Acme Inc"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Plan" label-for="crm-plan">
                    <b-form-select
                        id="crm-plan"
                        v-model="addCrmForm.plan"
                        :options="addCrmPlanOptions"
                    ></b-form-select>
                </b-form-group>
                <b-form-group label="Status" label-for="crm-status">
                    <b-form-select
                        id="crm-status"
                        v-model="addCrmForm.status"
                        :options="addCrmStatusOptions"
                    ></b-form-select>
                </b-form-group>
                <b-form-group label="MRR ($)" label-for="crm-mrr">
                    <b-form-input
                        id="crm-mrr"
                        v-model.number="addCrmForm.mrr"
                        type="number"
                        min="0"
                        placeholder="0"
                    ></b-form-input>
                </b-form-group>
            </b-form>
            <div class="modal-actions">
                <b-button
                    variant="outline-primary"
                    @click="$refs.addCrmModal.hide()"
                >
                    Cancel
                </b-button>
                <b-button
                    variant="primary"
                    @click="handleAddCrm"
                    :disabled="!addCrmForm.name"
                >
                    Add to CRM
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import ComponentHeader from "@/components/componentNewHeader";
import ActionButtonGroup from "@/components/Common/ActionButtonGroup.vue";
import { AtomSpinner } from "epic-spinners";

export default {
    name: "CrmList",
    components: {
        ComponentHeader,
        ActionButtonGroup,
        AtomSpinner,
    },
    data() {
        return {
            loading: false,
            searchQuery: "",
            statusFilter: null,
            planFilter: null,
            statusOptions: [
                { value: null, text: "All Status" },
                { value: "ACTIVE", text: "Active" },
                { value: "TRIAL", text: "Trial" },
                { value: "CANCELLED", text: "Cancelled" },
                { value: "PAST DUE", text: "Past Due" },
            ],
            planOptions: [
                { value: null, text: "All Plans" },
                { value: "Basic", text: "Basic" },
                { value: "Pro", text: "Pro" },
                { value: "Enterprise", text: "Enterprise" },
            ],
            tableFields: [
                { key: "account", label: "ACCOUNT", thClass: "table-header" },
                { key: "plan", label: "PLAN", thClass: "table-header" },
                { key: "status", label: "STATUS", thClass: "table-header" },
                { key: "mrr", label: "MRR", thClass: "table-header" },
                {
                    key: "lastPayment",
                    label: "LAST PAYMENT",
                    thClass: "table-header",
                },
                { key: "tickets", label: "TICKETS", thClass: "table-header" },
                { key: "actions", label: "ACTIONS", thClass: "table-header" },
            ],
            crmList: [
                {
                    id: "9082",
                    initials: "BI",
                    name: "Biswarup Roy",
                    company: "Calonex Solutions",
                    plan: "Enterprise",
                    status: "ACTIVE",
                    mrr: 1200,
                    lastPayment: "2026-02-01",
                    tickets: 2,
                },
                {
                    id: "9083",
                    initials: "AL",
                    name: "Alice Wong",
                    company: "FinTech IO",
                    plan: "Pro",
                    status: "TRIAL",
                    mrr: 0,
                    lastPayment: "-",
                    tickets: 0,
                },
                {
                    id: "9084",
                    initials: "MI",
                    name: "Michael Chen",
                    company: "Globex Corp",
                    plan: "Pro",
                    status: "PAST DUE",
                    mrr: 450,
                    lastPayment: "2025-12-15",
                    tickets: 1,
                },
            ],
            addCrmForm: {
                name: "",
                company: "",
                plan: "Pro",
                status: "TRIAL",
                mrr: 0,
            },
            addCrmPlanOptions: [
                { value: "Basic", text: "Basic" },
                { value: "Pro", text: "Pro" },
                { value: "Enterprise", text: "Enterprise" },
            ],
            addCrmStatusOptions: [
                { value: "ACTIVE", text: "Active" },
                { value: "TRIAL", text: "Trial" },
                { value: "CANCELLED", text: "Cancelled" },
                { value: "PAST DUE", text: "Past Due" },
            ],
        };
    },
    computed: {
        filteredCrmList() {
            let list = this.crmList.slice();
            const q = (this.searchQuery || "").toLowerCase().trim();

            if (q) {
                list = list.filter(
                    (item) =>
                        (item.name && item.name.toLowerCase().includes(q)) ||
                        (item.company &&
                            item.company.toLowerCase().includes(q)) ||
                        (item.initials &&
                            item.initials.toLowerCase().includes(q)),
                );
            }

            if (this.statusFilter) {
                list = list.filter((item) => item.status === this.statusFilter);
            }

            if (this.planFilter) {
                list = list.filter((item) => item.plan === this.planFilter);
            }

            return list;
        },
    },
    methods: {
        viewCRM(item) {
            this.$router.push({
                name: "crm-view",
                params: { id: item.id },
            });
        },
        showAddCrmModal() {
            this.$refs.addCrmModal.show();
        },
        resetAddCrmForm() {
            this.addCrmForm = {
                name: "",
                company: "",
                plan: "Pro",
                status: "TRIAL",
                mrr: 0,
            };
        },
        getInitials(name) {
            if (!name || !name.trim()) return "??";
            const parts = name.trim().split(/\s+/);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
            }
            return name.substring(0, 2).toUpperCase();
        },
        handleAddCrm() {
            if (!this.addCrmForm.name) return;
            const newId = String(9000 + this.crmList.length + 1);
            const newRecord = {
                id: newId,
                initials: this.getInitials(this.addCrmForm.name),
                name: this.addCrmForm.name,
                company: this.addCrmForm.company || "-",
                plan: this.addCrmForm.plan,
                status: this.addCrmForm.status,
                mrr: this.addCrmForm.mrr || 0,
                lastPayment: "-",
                tickets: 0,
            };
            this.crmList.unshift(newRecord);
            this.$refs.addCrmModal.hide();
            this.resetAddCrmForm();
            if (this.$bvToast) {
                this.$bvToast.toast("CRM record added successfully", {
                    title: "Success",
                    variant: "success",
                });
            }
        },
    },
};
</script>

<style scoped>
.crm-page {
    min-height: 100%;
}

.crm-subtitle {
    font-weight: 600;
    font-size: 12px;
    color: var(--slate-500, #64748b);
    margin: 6px 0 0;
}

.card-wrap {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.08);
    border-radius: 12px;
}

.crm-filters-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex-shrink: 0;
}

.filter-select {
    min-width: 160px;
    height: 34px;
    font-size: 14px;
    border-radius: 8px;
    border: 1px solid var(--slate-200, #e2e8f0);
}

.crm-table-card {
    border: 1px solid var(--slate-200, #e2e8f0);
}

.table-wrapper {
    overflow-x: auto;
    width: 100%;
}

::v-deep #crm-table thead th {
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: var(--slate-700, #334155);
    text-transform: uppercase;
    background: var(--slate-50, #f8fafc);
    border-bottom: 1px solid rgba(226, 232, 240, 0.8);
    padding: 14px 16px;
}

::v-deep #crm-table tbody td {
    padding: 16px;
    vertical-align: middle;
    border-bottom: 1px solid #f1f3f5;
}

::v-deep #crm-table tbody tr:hover {
    background-color: #f8f9fa;
}

::v-deep #crm-table tbody tr:last-child td {
    border-bottom: 0;
}

.account-cell {
    display: flex;
    align-items: center;
    gap: 12px;
}

.avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #e3f2fd;
    color: #1565c0;
    font-size: 0.85rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.account-info {
    display: flex;
    flex-direction: column;
}

.account-name {
    font-weight: 700;
    color: #1a1d21;
}

.account-company {
    font-size: 0.85rem;
    color: #6c757d;
}

.plan-badge {
    color: var(--slate-700, #334155);
    font-weight: 600;
    padding: 4px 10px;
}

.status-badge {
    padding: 4px 10px;
    font-weight: 600;
}

.status-active {
    background-color: #d4edda !important;
    color: #155724 !important;
}

.status-trial {
    background-color: #cce5ff !important;
    color: #004085 !important;
}

.status-past-due {
    background-color: #f8d7da !important;
    color: #721c24 !important;
}

.status-cancelled {
    background-color: #e2e3e5 !important;
    color: #383d41 !important;
}

.mrr-value {
    font-weight: 700;
    color: #1a1d21;
}

.last-payment {
    color: #495057;
}

.tickets-open {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #dc3545;
    font-size: 0.9rem;
}

.ticket-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #dc3545;
}

.tickets-none {
    color: #6c757d;
    font-size: 0.9rem;
}

.modal-subtitle {
    font-size: 12px;
    color: var(--slate-500, #64748b);
    margin: 0 0 0.75rem;
    padding: 0.75rem 1.5rem 0;
    text-align: center;
}

.modal-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    padding: 0 1.5rem 1.25rem;
}

.modal-actions .btn {
    min-width: 140px;
    border-radius: 10px;
    font-weight: 600;
}

@media (max-width: 768px) {
    .filter-select {
        min-width: 0;
        width: 100%;
        height: 40px;
        padding: 0.375rem 0.75rem;
        font-size: 0.9rem;
    }
}
</style>
